import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../../hooks/usePermissions';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  AlertTitle,
  Grid,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Tooltip
} from '@mui/material';
import {
  FileDownload as FileDownloadIcon,
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import productPricesReportService, {
  ProductPricesReportItem
} from '../../../services/productPricesReportService';
import * as XLSX from 'xlsx';

// تعريف الواجهات
interface Category {
  _id: string;
  name: string;
  description?: string;
}

interface FilterData {
  barcode: string;
  productName: string;
  category: string;
}

interface PriceItem {
  id: string;
  barcode: string;
  productName: string;
  variant: string;
  category: string;
  allCategories: string[];
  categoryCount: number;
  unit: string;
  retailPrice: number;
  wholesalePrice: number;
  customPrice1: number;
  customPrice2: number;
  lastCost: number;
  averageCost: number;
}

const ProductPricesReport: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  
  // الصلاحيات المطلوبة
  const hasProductPricesReportPermission = hasPermission('reports', 'product-prices', 'view_page');
  const hasProductPricesExportPermission = hasPermission('reports', 'product-prices', 'export_excel');

  // حالات المكون
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [pricesData, setPricesData] = useState<PriceItem[]>([]);
  const [categoriesList, setCategoriesList] = useState<{ id: string; name: string }[]>([]);
  const [filterValues, setFilterValues] = useState<FilterData>({
    barcode: '',
    productName: '',
    category: ''
  });

  // حالات التصفح (server-side pagination)
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(50);
  const [totalItems, setTotalItems] = useState<number>(0);

  // تحميل البيانات الأولية
  useEffect(() => {
    const initializeData = async () => {
      try {
        let catList: { id: string; name: string }[] = [];

        // جلب التصنيفات
        try {
          console.log('🔄 بدء جلب التصنيفات من API...');
          const categoriesResponse = await productPricesReportService.getCategories();
          console.log('📥 استجابة API التصنيفات:', categoriesResponse);
          
          if (categoriesResponse && categoriesResponse.success && categoriesResponse.data && Array.isArray(categoriesResponse.data)) {
            catList = categoriesResponse.data.map(cat => ({
              id: cat._id,
              name: cat.name
            }));
            setCategoriesList(catList);
            console.log('✅ تم جلب التصنيفات من API:', catList);
          } else if (categoriesResponse && categoriesResponse.data && Array.isArray(categoriesResponse.data)) {
            catList = categoriesResponse.data.map(cat => ({
              id: cat._id,
              name: cat.name
            }));
            setCategoriesList(catList);
            console.log('✅ تم جلب التصنيفات (بدون success flag):', catList);
          } else {
            console.warn('⚠️ استجابة API التصنيفات غير صحيحة:', categoriesResponse);
            setCategoriesList([]);
          }
        } catch (categoriesError) {
          console.error('❌ فشل جلب التصنيفات من API:', categoriesError);
          setCategoriesList([]);
        }

        // جلب بيانات الأسعار
        await loadPricesData(catList);

      } catch (error) {
        console.error('خطأ في تحميل البيانات:', error);
        setError('حدث خطأ أثناء تحميل البيانات.');
        setPricesData([]);
        setCategoriesList([]);
        setLoading(false);
      }
    };
    
    initializeData();
  }, []);

  // تحميل بيانات الأسعار عند تغيير الصفحة أو عدد الصفوف أو الفلاتر
  useEffect(() => {
    if (categoriesList.length >= 0) {
      loadPricesData(categoriesList);
    }
  }, [page, rowsPerPage, filterValues]);

  // دالة تحميل بيانات الأسعار
  const loadPricesData = async (catList: { id: string; name: string }[]) => {
    try {
      setLoading(true);
      
      // بناء الفلاتر للإرسال للسيرفر
      const filters: any = {
        page: page + 1,
        limit: rowsPerPage,
        sortBy: 'productName',
        sortOrder: 'asc'
      };

      // إضافة الفلاتر إذا كانت موجودة
      if (filterValues.barcode) {
        filters.search = filterValues.barcode;
      }
      if (filterValues.productName) {
        filters.search = filterValues.productName;
      }
      if (filterValues.category) {
        filters.category = filterValues.category;
      }
      
      console.log('🚀 بدء تحميل بيانات الأسعار...');
      const response = await productPricesReportService.getProductPricesReport(filters);
      
      if (!response.data || !Array.isArray(response.data)) {
        setPricesData([]);
        setTotalItems(0);
        setLoading(false);
        return;
      }

      // تحديث إجمالي عدد العناصر
      if (response.pagination) {
        setTotalItems(response.pagination.total);
      }

      // معالجة بيانات الأسعار
      const priceItems: PriceItem[] = [];

      console.log('✅ تم تحميل البيانات:', {
        totalItems: response.data.length,
        firstItem: response.data[0],
        pagination: response.pagination
      });

      for (const reportItem of response.data) {
        try {
          // استخراج الباركود
          const defaultBarcode = reportItem.barcode || 'غير متوفر';

          // إنشاء اسم المتغير من الخصائص
          const variantName = Object.values(reportItem.variantAttributes || {}).join(' - ') || 'غير محدد';

          // استخراج التصنيفات
          let categoryName = 'غير مصنف';
          let allCategories: string[] = [];
          let categoryCount = 0;
          
          if (reportItem.categories && reportItem.categories.length > 0) {
            allCategories = reportItem.categories.map(cat => cat.name || 'غير مسمى');
            categoryCount = allCategories.length;
            
            if (categoryCount === 1) {
              categoryName = allCategories[0];
            } else {
              categoryName = `${allCategories[0]} +${categoryCount - 1}`;
            }
          }

          // استخراج الأسعار من الباركود الافتراضي
          const defaultBarcodeData = reportItem.defaultBarcode || {};
          
          priceItems.push({
            id: reportItem._id || `item-${priceItems.length + 1}`,
            barcode: defaultBarcode,
            productName: reportItem.productName || reportItem.product?.name || 'غير محدد',
            variant: variantName,
            category: categoryName,
            allCategories: allCategories,
            categoryCount: categoryCount,
            unit: defaultBarcodeData.unit || reportItem.unit || 'قطعة',
            retailPrice: defaultBarcodeData.retailPrice || 0,
            wholesalePrice: defaultBarcodeData.wholesalePrice || 0,
            customPrice1: defaultBarcodeData.customPrice1 || 0,
            customPrice2: defaultBarcodeData.customPrice2 || 0,
            lastCost: defaultBarcodeData.lastCost || 0,
            averageCost: defaultBarcodeData.averageCost || 0
          });

        } catch (itemError) {
          console.error(`خطأ في معالجة عنصر: ${reportItem._id}`, itemError);
        }
      }

      console.log('📊 عدد العناصر المحملة:', priceItems.length);
      setPricesData(priceItems);
      setLoading(false);

    } catch (error) {
      console.error('خطأ في تحميل بيانات الأسعار:', error);
      setError('حدث خطأ أثناء تحميل بيانات الأسعار. يحتاج إنشاء API في الخادم.');
      setLoading(false);
    }
  };

  // دالة التصفية
  const handleFilter = useCallback(() => {
    setPage(0);
  }, []);

  // دالة مسح الفلاتر
  const handleClearFilters = useCallback(() => {
    setFilterValues({
      barcode: '',
      productName: '',
      category: ''
    });
    setPage(0);
  }, []);

  // دالة تصدير Excel
  const handleExportExcel = useCallback(async () => {
    if (!hasProductPricesExportPermission) {
      setError('ليس لديك صلاحية لتصدير التقارير');
      return;
    }

    try {
      setLoading(true);
      
      // جلب جميع البيانات بدون pagination
      const response = await productPricesReportService.getProductPricesReport({
        page: 1,
        limit: 999999,
        sortBy: 'productName',
        sortOrder: 'asc'
      });

      if (!response.data || !Array.isArray(response.data)) {
        setError('فشل جلب البيانات للتصدير');
        setLoading(false);
        return;
      }

      // معالجة البيانات للتصدير
      const exportData = response.data.map(reportItem => {
        const defaultBarcodeData = reportItem.defaultBarcode || {};
        
        return {
          'الباركود': reportItem.barcode || 'غير متوفر',
          'اسم المنتج': reportItem.productName || reportItem.product?.name || 'غير محدد',
          'المتغير': Object.values(reportItem.variantAttributes || {}).join(' - ') || 'غير محدد',
          'التصنيف': reportItem.categories && reportItem.categories.length > 0
            ? reportItem.categories.map(cat => cat.name).join(', ')
            : 'غير مصنف',
          'الوحدة': defaultBarcodeData.unit || reportItem.unit || 'قطعة',
          'سعر التجزئة': defaultBarcodeData.retailPrice || 0,
          'سعر الجملة': defaultBarcodeData.wholesalePrice || 0,
          'السعر المخصص 1': defaultBarcodeData.customPrice1 || 0,
          'السعر المخصص 2': defaultBarcodeData.customPrice2 || 0,
          'آخر تكلفة': defaultBarcodeData.lastCost || 0,
          'متوسط التكلفة': defaultBarcodeData.averageCost || 0
        };
      });

      // إنشاء ورقة العمل
      const ws = XLSX.utils.json_to_sheet(exportData);
      
      // تطبيق تنسيق RTL
      if (!ws['!cols']) ws['!cols'] = [];
      const colCount = Object.keys(exportData[0] || {}).length;
      for (let i = 0; i < colCount; i++) {
        ws['!cols'][i] = { wch: 15 };
      }

      // إنشاء المصنف
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'تقرير أسعار المنتجات');

      // تصدير الملف
      const fileName = `تقرير_أسعار_المنتجات_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);

      console.log('تم تصدير تقرير الأسعار بنجاح - عدد الصفوف:', exportData.length);
      setLoading(false);
    } catch (error) {
      console.error('خطأ في تصدير Excel:', error);
      setError('حدث خطأ أثناء تصدير البيانات');
      setLoading(false);
    }
  }, [hasProductPricesExportPermission]);

  // دالة تغيير الصفحة
  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // دالة تغيير عدد الصفوف في الصفحة
  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // عرض رسالة الخطأ إذا لم تكن هناك صلاحيات
  if (!hasProductPricesReportPermission) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          <AlertTitle>غير مصرح</AlertTitle>
          ليس لديك صلاحية للوصول إلى هذه الصفحة
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* العنوان والأزرار */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          تقرير أسعار المنتجات
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportExcel}
            disabled={!hasProductPricesExportPermission || loading}
          >
            تصدير Excel
          </Button>
        </Box>
      </Box>

      {/* رسالة الخطأ */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* مرشح البيانات */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          {/* الباركود */}
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="الباركود"
              value={filterValues.barcode}
              onChange={(e) => setFilterValues(prev => ({ ...prev, barcode: e.target.value }))}
              onKeyPress={(e) => e.key === 'Enter' && handleFilter()}
              placeholder="ابحث بالباركود"
              fullWidth
              size="small"
            />
          </Grid>

          {/* اسم المنتج */}
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="اسم المنتج"
              value={filterValues.productName}
              onChange={(e) => setFilterValues(prev => ({ ...prev, productName: e.target.value }))}
              onKeyPress={(e) => e.key === 'Enter' && handleFilter()}
              placeholder="ابحث باسم المنتج"
              fullWidth
              size="small"
            />
          </Grid>

          {/* التصنيف */}
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              select
              label="التصنيف"
              value={filterValues.category}
              onChange={(e) => setFilterValues(prev => ({ ...prev, category: e.target.value }))}
              fullWidth
              size="small"
            >
              <MenuItem value="">الكل</MenuItem>
              {categoriesList.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* أزرار البحث والمسح */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={handleClearFilters}
              >
                مسح الفلاتر
              </Button>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleFilter}
              >
                بحث
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* جدول البيانات */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>الباركود</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 200 }}>اسم المنتج</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>المتغير</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>التصنيف</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>الوحدة</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 120, bgcolor: '#e3f2fd' }}>سعر التجزئة</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 120, bgcolor: '#f3e5f5' }}>سعر الجملة</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 120, bgcolor: '#fff3e0' }}>السعر المخصص 1</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 120, bgcolor: '#fce4ec' }}>السعر المخصص 2</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 120, bgcolor: '#e8f5e9' }}>آخر تكلفة</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 120, bgcolor: '#f1f8e9' }}>متوسط التكلفة</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pricesData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      <Typography variant="body1" color="textSecondary" sx={{ py: 4 }}>
                        لا توجد بيانات لعرضها
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  pricesData.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>{item.barcode}</TableCell>
                      <TableCell>{item.productName}</TableCell>
                      <TableCell>{item.variant}</TableCell>
                      <TableCell>
                        {item.categoryCount > 1 ? (
                          <Tooltip title={item.allCategories.join(', ')}>
                            <Chip 
                              label={item.category} 
                              size="small" 
                              color="primary" 
                              variant="outlined"
                            />
                          </Tooltip>
                        ) : (
                          <Chip 
                            label={item.category} 
                            size="small" 
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                      <TableCell>{item.unit}</TableCell>
                      <TableCell align="center" sx={{ bgcolor: '#e3f2fd' }}>{item.retailPrice.toFixed(2)}</TableCell>
                      <TableCell align="center" sx={{ bgcolor: '#f3e5f5' }}>{item.wholesalePrice.toFixed(2)}</TableCell>
                      <TableCell align="center" sx={{ bgcolor: '#fff3e0' }}>{item.customPrice1.toFixed(2)}</TableCell>
                      <TableCell align="center" sx={{ bgcolor: '#fce4ec' }}>{item.customPrice2.toFixed(2)}</TableCell>
                      <TableCell align="center" sx={{ bgcolor: '#e8f5e9' }}>{item.lastCost.toFixed(2)}</TableCell>
                      <TableCell align="center" sx={{ bgcolor: '#f1f8e9' }}>{item.averageCost.toFixed(2)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={totalItems}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            labelRowsPerPage="عدد الصفوف في الصفحة:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} من ${count}`}
          />
        </Paper>
      )}
    </Box>
  );
};

export default ProductPricesReport;
